import React, { useState } from "react";
import "./AllCompanyContact.css";
import { toast } from "react-toastify";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import markerRedIcon from "../../assets/markerred.png"; // adjust path as needed

const customMarkerIcon = new L.Icon({
  iconUrl: markerRedIcon,
  iconSize: [52, 52], // adjust size as per your image
  iconAnchor: [16, 40], // point of the icon which will correspond to marker's location
  popupAnchor: [0, -40], // point from which the popup should open relative to the iconAnchor
  shadowUrl: null,
});

const AllCompanyContact = ({ office, fields, apiForm, map }) => {
  const initialFormData = fields
    .filter((field) => field.type !== "button")
    .reduce((acc, field) => {
      acc[field.name] = "";
      return acc;
    }, {});

  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "phone" ? value.replace(/\D/g, "") : value,
    }));
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isValidPhone = (phone) => /^[0-9]{10,15}$/.test(phone);

  const handleSubmit = async () => {
    if (
      !formData.name ||
      !formData.email ||
      !formData.phone ||
      !formData.subject
    ) {
      toast.warn("Please fill all required fields.");
      return;
    }

    if (!isValidEmail(formData.email)) {
      toast.error("Invalid email format.");
      return;
    }

    if (!isValidPhone(formData.phone)) {
      toast.error("Invalid phone number.");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(apiForm, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      let responseData;
      const contentType = response.headers.get("content-type");

      if (contentType && contentType.includes("application/json")) {
        responseData = await response.json();
      } else {
        responseData = { message: "Unexpected response from server." };
      }

      if (response.ok) {
        toast.success("Form submitted successfully!");
        setFormData(initialFormData);
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 4000);
      } else {
        toast.error(
          responseData.message || "Failed to submit. Please try again."
        );
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
      console.error("❌ Submission error:", error);
    }

    setIsSubmitting(false);
  };

  return (
    <div className="allcontact__container">
      {/* Office Info */}
      <div className="allcontact__column">
        <h3>{office.type?.toUpperCase()}</h3>
        <div className="allcontact__underline" />
        {office.address && (
          <p>
            <i className="fas fa-map-marker-alt icon-blue"></i>{" "}
            <a
              href={office.addressLink || map}
              target="_blank"
              rel="noopener noreferrer"
               className="allcontact__address-link"
            >
              {office.address}
            </a>
          </p>
        )}
        {office.contact && (
          <p>
            <i className="fas fa-phone icon-blue"></i> <strong>Contact:</strong>{" "}
            {office.contact}
          </p>
        )}
        {office.email && (
          <p>
            <i className="fas fa-envelope icon-blue"></i>{" "}
            <strong>Email:</strong> {office.email}
          </p>
        )}
      </div>

      {/* Map */}
      <div className="allcontact__column">
        <h3>FIND US</h3>
        <div className="allcontact__underline" />
        <div className="allcontact__map-container">
          <MapContainer
            center={[18.5602, 73.8077]}
            zoom={15}
            scrollWheelZoom={false}
            style={{ height: "300px", width: "100%", borderRadius: "10px" }}
          >
            <TileLayer
              attribution="&copy; OpenStreetMap contributors"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker position={[18.5602, 73.8077]} icon={customMarkerIcon}>
              <Popup>
                <strong>ASP Water & Power Projects Pvt. Ltd</strong>
                <br />
                <a
                  href="https://www.google.com/maps?q=18.5602,73.8077"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#e63946",
                    fontWeight: "bold",
                    textDecoration: "underline",
                  }}
                >
                  Open in Google Maps
                </a>
              </Popup>
            </Marker>
          </MapContainer>
        </div>
      </div>

      {/* Contact Form */}
      <div className="allcontact__column">
        {fields.map((field, idx) => {
          if (field.type === "textarea") {
            return (
              <textarea
                key={idx}
                name={field.name}
                placeholder={field.placeholder}
                rows={field.rows || 5}
                required={field.required}
                value={formData[field.name]}
                onChange={handleChange}
                className="allcontact__textarea"
              />
            );
          } else if (field.type === "button") {
            return (
              <button
                key={idx}
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="allcontact__button"
              >
                {isSubmitting ? "Sending..." : field.label}
              </button>
            );
          } else {
            const inputProps = {
              type: field.type,
              name: field.name,
              placeholder: field.placeholder,
              required: field.required,
              value: formData[field.name],
              onChange: handleChange,
              className: "allcontact__input",
            };

            if (field.name === "phone") {
              inputProps.inputMode = "numeric";
              inputProps.pattern = "[0-9]*";
              inputProps.maxLength = 10;
              inputProps.title = "Please enter a 10-digit phone number";
            }

            return <input key={`${field.name}-${idx}`} {...inputProps} />;
          }
        })}
      </div>

      {/* Success Popup */}
      {showPopup && (
        <div className="allcontact__popup-overlay">
          <div className="allcontact__popup-message">
            <h3>✅ Message Sent</h3>
            <p>Thank you for contacting us. We’ll get back to you shortly!</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllCompanyContact;
